package com.iregained.site.components.sections.distributors.sections

import androidx.compose.runtime.*
import com.iregained.site.components.sections.distributors.styles.DistributorListItemStyle
import com.iregained.site.components.sections.distributors.styles.LocatorSearchInputStyle
import com.iregained.site.components.sections.distributors.styles.LocatorTwoPaneContainerStyle
import com.iregained.site.region.RegionConfig
import com.iregained.site.theme.toSitePalette
import com.iregained.site.utils.LocalBreakpoint
import com.iregained.site.utils.Res
import com.varabyte.kobweb.browser.dom.observers.IntersectionObserver
import com.varabyte.kobweb.compose.css.*
import com.varabyte.kobweb.compose.css.Transition
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.graphics.Image
import com.varabyte.kobweb.silk.components.navigation.Link
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.css.AlignItems
import org.jetbrains.compose.web.dom.A
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Iframe
import org.jetbrains.compose.web.dom.Input

// UNIQUE ANIMATIONS: Pincer Converge
@Composable
private fun Modifier.pincerLeft(isVisible: Boolean): Modifier {
    return this.opacity(if (isVisible) 1 else 0).translateX(if (isVisible) 0.px else (-50).px).transition(
        Transition.of("opacity", 0.8.s, TransitionTimingFunction.EaseOut),
        Transition.of("transform", 0.8.s, TransitionTimingFunction.EaseOut)
    )
}

@Composable
private fun Modifier.pincerRight(isVisible: Boolean): Modifier {
    return this.opacity(if (isVisible) 1 else 0).translateX(if (isVisible) 0.px else 50.px).transition(
        Transition.of("opacity", 0.8.s, TransitionTimingFunction.EaseOut),
        Transition.of("transform", 0.8.s, TransitionTimingFunction.EaseOut)
    )
}

@Composable
fun DistributorsListSection(data: DistributorLocatorData = defaultDistributorData) {
    val breakpoint = LocalBreakpoint.current
    val isDesktop = breakpoint >= Res.Breakpoints.LG

    var searchQuery by remember { mutableStateOf("") }
    var activeDistributor by remember { mutableStateOf<DistributorData?>(null) }
    var isVisible by remember { mutableStateOf(false) }
    val palette = ColorMode.current.toSitePalette()

    val filteredDistributors = remember(searchQuery, data.activeDistributors) {
        if (searchQuery.isBlank()) data.activeDistributors
        else data.activeDistributors.filter {
            it.companyName.contains(searchQuery, ignoreCase = true) || it.address.contains(
                searchQuery, ignoreCase = true
            )
        }
    }

    Column(
        modifier = Modifier.id(data.listSectionId ?: "distributors-list-section")
            .styleModifier { property("scroll-margin-top", "100px") }.fillMaxWidth()
            .background(palette.custom.lightGray), horizontalAlignment = Alignment.CenterHorizontally
    ) {
        Div(
            attrs = Modifier.fillMaxWidth().display(DisplayStyle.Flex)
                .justifyContent(org.jetbrains.compose.web.css.JustifyContent.Center).toAttrs {
                    ref { element ->
                        val observer = IntersectionObserver { entries, obs ->
                            entries.forEach {
                                if (it.isIntersecting) {
                                    isVisible = true; obs.unobserve(element)
                                }
                            }
                        }
                        observer.observe(element); onDispose { observer.disconnect() }
                    }
                }) {
            Box(modifier = LocatorTwoPaneContainerStyle.toModifier()) {
                if (isDesktop) {
                    Row(modifier = Modifier.fillMaxWidth().height(750.px)) {
                        LocatorListPane(
                            data = data,
                            isDesktop = true,
                            searchQuery = searchQuery,
                            onSearchChange = { searchQuery = it },
                            distributors = filteredDistributors,
                            onDistributorClick = { activeDistributor = it },
                            modifier = Modifier.width(50.percent).fillMaxHeight().pincerLeft(isVisible)
                        )
                        LocatorMapPane(
                            distributors = filteredDistributors,
                            activeDistributor = activeDistributor,
                            modifier = Modifier.width(50.percent).fillMaxHeight().pincerRight(isVisible)
                        )
                    }
                } else {
                    Column(modifier = Modifier.fillMaxWidth()) {
                        LocatorListPane(
                            data = data,
                            isDesktop = false,
                            searchQuery = searchQuery,
                            onSearchChange = { searchQuery = it },
                            distributors = filteredDistributors,
                            onDistributorClick = { activeDistributor = it },
                            modifier = Modifier.fillMaxWidth().height(500.px).pincerLeft(isVisible)
                        )
                        LocatorMapPane(
                            distributors = filteredDistributors,
                            activeDistributor = activeDistributor,
                            modifier = Modifier.fillMaxWidth().height(400.px)
                                .pincerRight(isVisible) // Slides in from right on mobile too
                        )
                    }
                }
            }
        }
    }
}

@Composable
fun LocatorListPane(
    data: DistributorLocatorData,
    isDesktop: Boolean,
    searchQuery: String,
    onSearchChange: (String) -> Unit,
    distributors: List<DistributorData>,
    onDistributorClick: (DistributorData) -> Unit,
    modifier: Modifier
) {
    val palette = ColorMode.current.toSitePalette()

    // Dynamic border depending on mobile vs desktop
    val dynamicBorderModifier = if (isDesktop) {
        Modifier.borderRight(1.px, LineStyle.Solid, palette.custom.divider)
    } else {
        Modifier.borderBottom(4.px, LineStyle.Solid, palette.custom.divider)
    }

    Column(
        modifier = modifier.backgroundColor(Colors.White).then(dynamicBorderModifier)
    ) {
        // Search Input Header
        Column(modifier = Modifier.fillMaxWidth().backgroundColor(palette.custom.lightGray).padding(20.px)) {
            Input(
                type = InputType.Text, attrs = LocatorSearchInputStyle.toModifier().toAttrs {
                    attr("placeholder", data.searchPlaceholder)
                    value(searchQuery)
                    onInput { e -> onSearchChange(e.value) }
                })
        }

        // Results Count Header
        Box(
            modifier = Modifier.fillMaxWidth().backgroundColor(palette.brand.primary)
                .padding(topBottom = 12.px, leftRight = 20.px).boxShadow(0.px, 4.px, 10.px, color = rgba(0, 0, 0, 0.1f))
                .zIndex(2)
        ) {
            SpanText(
                "${distributors.size} ${data.locationsFoundText}",
                modifier = Modifier.color(Colors.White).fontWeight(FontWeight.Bold).fontSize(14.px)
            )
        }

        // Scrollable List
        Column(modifier = Modifier.fillMaxWidth().weight(1f).styleModifier { overflowY(Overflow.Auto) }) {

            if (distributors.isEmpty()) {
                if (searchQuery.isNotBlank()) {
                    // Standard empty search result
                    SpanText(
                        text = data.noResultsMessage,
                        modifier = Modifier.padding(24.px).fontSize(16.px).color(palette.custom.darkText)
                            .textAlign(TextAlign.Center)
                    )
                } else {
                    // Fancy Empty State CTA (When a region has zero distributors configured)
                    Column(
                        modifier = Modifier.fillMaxSize().padding(32.px),
                        horizontalAlignment = Alignment.CenterHorizontally,
                        verticalArrangement = Arrangement.Center
                    ) {
                        // Re-using the background image as a circular icon. You can swap this for a globe or map icon if you prefer!
                        Image(
                            src = data.emptyStateImage,
                            modifier = Modifier.size(80.px).margin(bottom = 24.px)
                                .styleModifier { property("object-fit", "contain") })
                        SpanText(
                            text = data.emptyStateHeadline,
                            modifier = Modifier.fontSize(24.px).fontWeight(FontWeight.Bold).color(palette.brand.primary)
                                .margin(bottom = 12.px).textAlign(TextAlign.Center)
                        )
                        SpanText(
                            text = data.emptyStateDescription,
                            modifier = Modifier.fontSize(16.px).color(palette.custom.darkText).lineHeight(1.6)
                                .margin(bottom = 32.px).textAlign(TextAlign.Center)
                        )
                        Link(
                            path = data.ctaLink, modifier = Modifier.textDecorationLine(TextDecorationLine.None)
                        ) {
                            Box(
                                modifier = Modifier.backgroundColor(palette.brand.primary).color(Colors.White)
                                    .borderRadius(8.px).padding(topBottom = 12.px, leftRight = 24.px)
                                    .cursor(Cursor.Pointer)
                            ) {
                                SpanText(
                                    text = data.emptyStateButtonText,
                                    modifier = Modifier.fontSize(16.px).fontWeight(FontWeight.Bold)
                                )
                            }
                        }
                    }
                }
            } else {
                distributors.forEach { distributor ->
                    key(distributor.companyName) {
                        Column(
                            modifier = DistributorListItemStyle.toModifier()
                                .borderBottom(1.px, LineStyle.Solid, palette.custom.divider)
                                .onClick { onDistributorClick(distributor) }) {

                            Div(
                                attrs = Modifier.fontSize(20.px).color(palette.brand.primary)
                                    .fontWeight(FontWeight.Bold).margin(bottom = 4.px).toAttrs {
                                        ref { htmlElement ->
                                            htmlElement.innerHTML = distributor.companyName
                                            onDispose {}
                                        }
                                    })

                            SpanText(
                                distributor.address,
                                modifier = Modifier.fontSize(15.px).margin(bottom = 16.px)
                                    .color(palette.custom.darkText)
                            )

                            // Contact Icons
                            if (distributor.phone.isNotBlank()) {
                                A(
                                    href = "tel:${distributor.phone.replace(Regex("[^0-9+]"), "")}",
                                    attrs = Modifier.display(DisplayStyle.Flex).flexDirection(FlexDirection.Row)
                                        .alignItems(AlignItems.Center).gap(8.px)
                                        .textDecorationLine(TextDecorationLine.None).color(palette.custom.darkText)
                                        .margin(bottom = 12.px).toAttrs()
                                ) {
                                    Image(Res.Icons.CALL, Modifier.size(18.px).flexShrink(0))
                                    SpanText(
                                        text = distributor.phone,
                                        modifier = Modifier.fontSize(15.px).fontWeight(FontWeight.Medium)
                                            .whiteSpace(WhiteSpace.NoWrap)
                                    )
                                }
                            }

                            if (distributor.email.isNotBlank()) {
                                A(
                                    href = "mailto:${distributor.email}",
                                    attrs = Modifier.display(DisplayStyle.Flex).flexDirection(FlexDirection.Row)
                                        .alignItems(AlignItems.Center).gap(8.px)
                                        .textDecorationLine(TextDecorationLine.None).color(palette.custom.darkText)
                                        .margin(bottom = 12.px).toAttrs()
                                ) {
                                    Image(Res.Icons.SEND_EMAIL, Modifier.size(18.px).flexShrink(0))
                                    SpanText(
                                        text = distributor.email,
                                        modifier = Modifier.fontSize(15.px).fontWeight(FontWeight.Medium)
                                            .whiteSpace(WhiteSpace.NoWrap)
                                    )
                                }
                            }

                            // Action Buttons Row
                            Row(modifier = Modifier.fillMaxWidth().margin(top = 8.px).gap(12.px)) {
                                val encodedAddress = distributor.address.replace(" ", "+")
                                A(
                                    href = "https://www.google.com/maps/dir/?api=1&destination=${encodedAddress}",
                                    attrs = Modifier.textDecorationLine(TextDecorationLine.None)
                                        .toAttrs { attr("target", "_blank") }) {
                                    Box(
                                        modifier = Modifier.backgroundColor(palette.brand.primary).color(Colors.White)
                                            .borderRadius(6.px).padding(topBottom = 8.px, leftRight = 16.px)
                                    ) {
                                        SpanText(
                                            data.directionsButtonText,
                                            modifier = Modifier.fontSize(14.px).fontWeight(FontWeight.Bold)
                                        )
                                    }
                                }

                                if (distributor.websiteUrl.isNotBlank()) {
                                    A(
                                        href = distributor.websiteUrl,
                                        attrs = Modifier.textDecorationLine(TextDecorationLine.None)
                                            .toAttrs { attr("target", "_blank") }) {
                                        Box(
                                            modifier = Modifier.border(1.px, LineStyle.Solid, palette.brand.primary)
                                                .backgroundColor(Colors.Transparent).color(palette.brand.primary)
                                                .borderRadius(6.px).padding(topBottom = 8.px, leftRight = 16.px)
                                        ) {
                                            SpanText(
                                                data.websiteButtonText,
                                                modifier = Modifier.fontSize(14.px).fontWeight(FontWeight.Bold)
                                            )
                                        }
                                    }
                                }
                            }
                        }
                    }
                }
            }
        }
    }
}


@Composable
fun LocatorMapPane(
    distributors: List<DistributorData>, activeDistributor: DistributorData?, modifier: Modifier
) {
    val googleMapsApiKey = "AIzaSyCkCXKJB7hJtqb1bJGSjpiAGomBHkGCd-E"

    val countryQuery = when (RegionConfig.currentRegion) {
        com.iregained.site.region.Region.US -> "United States"
        com.iregained.site.region.Region.CANADA -> "Canada"
        com.iregained.site.region.Region.INDIA -> "India"
    }

    Box(modifier = modifier.backgroundColor(Colors.White)) {
        if (googleMapsApiKey.isBlank()) {
            // Fallback for when API Key is missing
            val query = activeDistributor?.address ?: distributors.firstOrNull()?.address ?: countryQuery
            val encodedQuery = query.replace(" ", "+")
            val defaultZoom = if (distributors.isEmpty()) "8" else "13"
            val mapEmbedUrl =
                "https://maps.google.com/maps?q=${encodedQuery}&t=&z=${defaultZoom}&ie=UTF8&iwloc=&output=embed"

            Iframe(
                attrs = Modifier.fillMaxSize().border(0.px).toAttrs {
                    attr("src", mapEmbedUrl)
                    attr("loading", "lazy")
                    attr("referrerpolicy", "no-referrer-when-downgrade")
                })
        } else {
            val locationsJson = distributors.joinToString(",\n") {
                "{ name: \"${it.companyName.replace("\"", "\\\"")}\", address: \"${
                    it.address.replace(
                        "\"", "\\\""
                    )
                }\" }"
            }
            val activeIdx = if (activeDistributor != null) distributors.indexOf(activeDistributor) else -1

            val htmlContent = """
                <!DOCTYPE html>
                <html>
                  <head>
                    <style>
                      html, body { height: 100%; margin: 0; padding: 0; font-family: sans-serif; }
                      #map { height: 100%; width: 100%; }
                    </style>
                  </head>
                  <body>
                    <div id="map"></div>
                    <script>
                      function initMap() {
                        var map = new google.maps.Map(document.getElementById('map'), {
                          zoom: 2,
                          center: {lat: 20, lng: 0} // Temporary global center
                        });
                        var geocoder = new google.maps.Geocoder();
                        var bounds = new google.maps.LatLngBounds();
                        var infowindow = new google.maps.InfoWindow();

                        var locations = [
                            $locationsJson
                        ];

                        // 2. NEW: If no locations exist, use Geocoder to zoom to the active country
                        if(locations.length === 0) {
                            geocoder.geocode({'address': '$countryQuery'}, function(results, status) {
                                if (status === 'OK') {
                                    // fitBounds perfectly frames the viewport of the specified country
                                    map.fitBounds(results[0].geometry.viewport); 
                                }
                            });
                            return;
                        }

                        var activeIndex = $activeIdx;
                        var processed = 0;
                        var markers = [];

                        locations.forEach(function(loc, i) {
                          geocoder.geocode({'address': loc.address}, function(results, status) {
                            processed++;
                            if (status === 'OK') {
                              var marker = new google.maps.Marker({
                                map: map,
                                position: results[0].geometry.location,
                                title: loc.name
                              });
                              markers.push(marker);
                              bounds.extend(results[0].geometry.location);

                              marker.addListener('click', function() {
                                infowindow.setContent('<div style="font-family:sans-serif; padding:4px;"><b>' + loc.name + '</b><br/>' + loc.address + '</div>');
                                infowindow.open(map, marker);
                              });

                              if (i === activeIndex) {
                                infowindow.setContent('<div style="font-family:sans-serif; padding:4px;"><b>' + loc.name + '</b><br/>' + loc.address + '</div>');
                                infowindow.open(map, marker);
                                map.setZoom(14);
                                map.panTo(marker.position);
                              }
                            }

                            if (processed === locations.length && activeIndex === -1) {
                              if (markers.length > 1) {
                                map.fitBounds(bounds);
                              } else if (markers.length === 1) {
                                map.setCenter(markers[0].position);
                                map.setZoom(13);
                              }
                            }
                          });
                        });
                      }
                    </script>
                    <script async defer src="https://maps.googleapis.com/maps/api/js?key=$googleMapsApiKey&callback=initMap&loading=async"></script>
                  </body>
                </html>
            """.trimIndent()

            Iframe(
                attrs = Modifier.fillMaxSize().border(0.px).toAttrs {
                    ref { el ->
                        val iframe = el as org.w3c.dom.HTMLIFrameElement
                        val doc = iframe.contentDocument
                        if (doc != null) {
                            val dynamicDoc = doc.asDynamic()
                            dynamicDoc.open()
                            dynamicDoc.write(htmlContent)
                            dynamicDoc.close()
                        }
                        onDispose {}
                    }
                })
        }
    }
}